import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { FieldLabel, Textarea, TextField } from '~/components/form';
import ModelSelect from '~/components/pages/ai/common/ModelSelect';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardSection,
  FormActions,
  Modal,
  Skeleton,
  SkeletonFields,
  LoadError,
} from '~/components/ui';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import {
  commitPromptForm,
  createPromptFormsState,
  DEFAULT_MODEL,
  editPromptForm,
  promptDefinitionsForGroup,
  promptServerFormsFromConfigs,
  resetPromptForm,
  syncPromptForms,
  type PromptConfigDefinition,
  type PromptConfigGroup,
} from '~/lib/ai/settingsForm';
import { useGetProvidersQuery } from '~/modules/query/ai/providers';
import {
  useCreateConfigMutation,
  useListConfigsQuery,
  useUpdateConfigMutation,
} from '~/modules/query/ai/settings';

export interface PromptConfigSectionProps {
  /** Which prompts this section owns: the four AI Write actions, or the prompt assistant. */
  group: PromptConfigGroup;
}

const SAVE_MODAL_FORM_ID = 'ai-settings-prompt-save-form';

/**
 * The application-level prompt templates. Each prompt is its own saved record:
 * an uninitialized one offers Initialize, a saved one offers Save changes behind
 * a required change note. Reset returns to the last saved (or default) text.
 */
export default function PromptConfigSection({ group }: PromptConfigSectionProps) {
  const configsQuery = useListConfigsQuery();
  const providersQuery = useGetProvidersQuery();
  const [createConfig, { isLoading: isCreating }] = useCreateConfigMutation();
  const [updateConfig, { isLoading: isSaving }] = useUpdateConfigMutation();

  const definitions = useMemo(() => promptDefinitionsForGroup(group), [group]);
  const savedComponentKeys = useMemo(
    () => new Set((configsQuery.data?.configs ?? []).map((config) => config.componentKey)),
    [configsQuery.data?.configs],
  );
  const serverForms = useMemo(
    () => promptServerFormsFromConfigs(configsQuery.data?.configs),
    [configsQuery.data?.configs],
  );

  const [forms, setForms] = useState(createPromptFormsState);
  const [saveTarget, setSaveTarget] = useState<PromptConfigDefinition | null>(null);
  const [changeNote, setChangeNote] = useState('');

  useEffect(() => {
    if (configsQuery.isLoading) {
      return;
    }

    setForms((previous) => syncPromptForms(previous, serverForms));
  }, [configsQuery.isLoading, serverForms]);

  const closeSaveModal = () => {
    setSaveTarget(null);
    setChangeNote('');
  };

  const initialize = async (definition: PromptConfigDefinition) => {
    const form = forms.drafts[definition.componentKey];

    if (!form?.promptTemplate.trim()) {
      toast.error('Add a prompt template before initializing the prompt.');
      return;
    }

    const model = form.model.trim() || DEFAULT_MODEL;

    try {
      await createConfig({
        componentKey: definition.componentKey,
        name: definition.name,
        description: definition.description,
        model,
        promptTemplate: form.promptTemplate,
        isActive: true,
      }).unwrap();
      setForms((previous) =>
        commitPromptForm(previous, definition.componentKey, {
          model,
          promptTemplate: form.promptTemplate,
        }),
      );
      toast.success(`${definition.title} initialized`);
    } catch (error) {
      toast.error(
        apiErrorMessage(error as ApiErrorInput) ??
          `The ${definition.title.toLowerCase()} prompt could not be initialized. Try again.`,
      );
    }
  };

  const openSaveModal = (definition: PromptConfigDefinition) => {
    const form = forms.drafts[definition.componentKey];

    if (!form?.promptTemplate.trim()) {
      toast.error('Add a prompt template before saving the prompt.');
      return;
    }

    setChangeNote('');
    setSaveTarget(definition);
  };

  const save = async () => {
    if (!saveTarget) {
      return;
    }

    const form = forms.drafts[saveTarget.componentKey];
    const note = changeNote.trim();

    if (!form?.promptTemplate.trim()) {
      toast.error('Add a prompt template before saving the prompt.');
      return;
    }

    if (!note) {
      return;
    }

    const model = form.model.trim() || DEFAULT_MODEL;

    try {
      await updateConfig({
        componentKey: saveTarget.componentKey,
        updates: { model, promptTemplate: form.promptTemplate },
        changeNote: note,
      }).unwrap();
      setForms((previous) =>
        commitPromptForm(previous, saveTarget.componentKey, {
          model,
          promptTemplate: form.promptTemplate,
        }),
      );
      closeSaveModal();
      toast.success('Prompt settings updated');
    } catch (error) {
      toast.error(
        apiErrorMessage(error as ApiErrorInput) ??
          'The prompt settings could not be saved. Try again.',
      );
    }
  };

  const models = providersQuery.data?.models;
  const modelsUnavailable = providersQuery.isError && !providersQuery.data;

  return (
    <>
      <Card>
        <CardHeader
          title={group === 'aiWrite' ? 'AI Write prompts' : 'Other prompts'}
          description={
            group === 'aiWrite'
              ? 'Application-level prompts used by the rewrite, shorten, expand and proofread placement actions.'
              : 'Application-level prompts used outside the placement actions.'
          }
        />
        <CardBody className="space-y-6">
          {configsQuery.error && !configsQuery.data ? (
            <LoadError
              what="the prompt settings"
              message={apiErrorMessage(configsQuery.error as ApiErrorInput)}
              onRetry={() => configsQuery.refetch()}
              testId="ai-settings-prompts"
            />
          ) : null}

          {configsQuery.isLoading ? (
            <SkeletonFields fields={definitions.length * 2} />
          ) : configsQuery.error && !configsQuery.data ? null : (
            definitions.map((definition) => {
              const form = forms.drafts[definition.componentKey];
              const dirty = Boolean(forms.dirty[definition.componentKey]);
              const saved = savedComponentKeys.has(definition.componentKey);
              const modelId = `${definition.componentKey}-model`;
              const templateId = `${definition.componentKey}-template`;
              const testPrefix = `ai-settings-prompt-${definition.componentKey}`;

              if (!form) {
                return null;
              }

              return (
                <CardSection
                  key={definition.componentKey}
                  title={definition.title}
                  description={definition.description}
                  testId={testPrefix}
                  actions={
                    <div className="flex items-center gap-3">
                      {saved ? (
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={!dirty || isSaving || configsQuery.isFetching}
                          title={!dirty ? 'No changes to save' : undefined}
                          onClick={() => openSaveModal(definition)}
                          testId={`${testPrefix}-save`}
                        >
                          Save changes
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          loading={isCreating}
                          onClick={() => {
                            void initialize(definition);
                          }}
                          testId={`${testPrefix}-initialize`}
                        >
                          Initialize prompt settings
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!dirty}
                        title={!dirty ? 'No changes to reset' : undefined}
                        onClick={() =>
                          setForms((previous) => resetPromptForm(previous, definition.componentKey))
                        }
                        testId={`${testPrefix}-reset`}
                      >
                        Reset
                      </Button>
                    </div>
                  }
                >
                  <div className="space-y-5">
                    {providersQuery.isLoading ? (
                      <div>
                        <FieldLabel htmlFor={modelId}>Model</FieldLabel>
                        <Skeleton className="h-11" />
                      </div>
                    ) : modelsUnavailable ? (
                      <TextField
                        id={modelId}
                        label="Model"
                        value={form.model}
                        onChange={() => undefined}
                        readOnly
                        helperText="The model catalog could not be loaded, so the model cannot be changed right now."
                        testId={`${testPrefix}-model`}
                      />
                    ) : (
                      <ModelSelect
                        id={modelId}
                        label="Model"
                        value={form.model}
                        onChange={(value) =>
                          setForms((previous) =>
                            editPromptForm(previous, definition.componentKey, { model: value }),
                          )
                        }
                        models={models}
                        required
                        helperText="The active model this prompt runs on."
                        testId={`${testPrefix}-model`}
                      />
                    )}

                    <Textarea
                      id={templateId}
                      label="Prompt template"
                      value={form.promptTemplate}
                      onChange={(value) =>
                        setForms((previous) =>
                          editPromptForm(previous, definition.componentKey, {
                            promptTemplate: value,
                          }),
                        )
                      }
                      rows={10}
                      code
                      required
                      helperText="This text is sent to the model as its instruction."
                      testId={`${testPrefix}-template`}
                    />
                  </div>
                </CardSection>
              );
            })
          )}
        </CardBody>
      </Card>

      <Modal
        isOpen={Boolean(saveTarget)}
        onClose={closeSaveModal}
        size="small"
        title="Save prompt changes"
        description={
          saveTarget
            ? `Add a change note before saving the update to ${saveTarget.title}.`
            : undefined
        }
        testId="ai-settings-prompt-save"
        busy={isSaving}
        footer={
          <FormActions
            form={SAVE_MODAL_FORM_ID}
            discardLabel="Cancel"
            onDiscard={closeSaveModal}
            saveLabel="Save changes"
            busy={isSaving}
            disabled={!changeNote.trim()}
            disabledReason="Add a change note to save"
            saveTestId="ai-settings-prompt-save-confirm"
            discardTestId="ai-settings-prompt-save-cancel"
          />
        }
      >
        <form
          id={SAVE_MODAL_FORM_ID}
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <Textarea
            id="ai-settings-prompt-change-note"
            label="Change note"
            value={changeNote}
            onChange={setChangeNote}
            rows={3}
            required
            placeholder="Describe what changed"
            helperText="Stored with the prompt's history so the next editor knows why it changed."
            testId="ai-settings-prompt-change-note"
          />
        </form>
      </Modal>
    </>
  );
}
